/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
} from '@atlassian/jira-help-panel-common-constant/src/index.tsx';
import { useHelpPanelActions } from '@atlassian/jira-help-panel-services/src/components/index.tsx';
import { useRightSidebarController } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';

type ArticleType = 'HELP_ARTICLE' | 'WHATS_NEW';

export interface HelpPanelButtonProps extends Omit<ButtonProps, 'type'> {
	articleId?: string;
	type?: ArticleType;
	children?: ReactNode;
	/**
	 * @deprecated No longer used. You can safely delete this prop.
	 */
	fallbackComponent?: ReactNode;
	/** href added to underlying link, note: articleId is used to open the help panel article, this link is for copying, Cmd+clicking, etc */
	href?: string;
}

export const useHelpPanelLink = (
	resetHistory = false,
): ((id: string, type?: ArticleType) => void) => {
	const { openRightSidebar } = useRightSidebarController();
	const [, { setNavigationData }] = useHelpPanelActions();

	const handleOnClick = (id: string, type: ArticleType = 'HELP_ARTICLE') => {
		openRightSidebar(HELP_PANEL_ID, HELP_PANEL_WIDTH, false, true);

		if (resetHistory) {
			setNavigationData({
				articleId: {
					id,
					type,
				},
				history: [],
			});
		} else {
			setNavigationData({
				articleId: {
					id,
					type,
				},
			});
		}
	};
	return handleOnClick;
};

export const HelpPanelLink = ({
	articleId = '',
	type = 'HELP_ARTICLE',
	children,
	onClick,
	href,
	fallbackComponent, // unused, but needs to be here so it's not passed to Link/Button in the ...props splat
	...props
}: HelpPanelButtonProps) => {
	const onOpenInProductHelpArticle = useHelpPanelLink();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleOnClick = (event: any, analyticsEvent: any) => {
		onClick?.(event, analyticsEvent);
		onOpenInProductHelpArticle(articleId, type);
	};

	return (
		<Button
			{...props}
			appearance={
				props.appearance === 'subtle-link' &&
				expValEquals('m1_features_page_experiment', 'cohort', 'variation')
					? 'link'
					: props.appearance
			}
			onClick={handleOnClick}
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
			css={[
				props.appearance === 'link' && buttonWrapperStyles,
				props.appearance === 'subtle-link' &&
					expValEquals('m1_features_page_experiment', 'cohort', 'variation') &&
					buttonWrapperSubtleStyles,
			]}
		>
			{children}
		</Button>
	);
};
/**
 * @deprecated Use named export instead.
 */
export default HelpPanelLink;

const buttonWrapperStyles = css({
	textDecoration: 'underline',
	'&:hover': {
		textDecoration: 'none',
	},
});

const buttonWrapperSubtleStyles = css({
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'underline',
	},
});
